import React, { useEffect, useRef, useState } from "react";
import { Alert, Col, Form, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { BsPlus, BsPencil, BsPrinter, BsSave, BsSearch, BsTrash, BsX } from "react-icons/bs";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../context/userAuth/UserAuthState";
import {
  deleteInvoiceAPI,
  fetchInvoiceListAPI,
  printInvoiceAPI,
  updateInvoiceAPI,
} from "../api/api";

const PER_PAGE = 10;

const STATUS_LABELS = {
  quotation: "Quotation",
  unpaid: "Unpaid",
  partial: "Partial",
  paid: "Paid",
  cancelled: "Cancelled",
};

const TYPE_LABELS = {
  invoice: "Cash Invoice",
  quotation: "Quotation",
};

const money = (n) =>
  Number(n || 0).toLocaleString("en-PK", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const InvoicesPage = () => {
  const { user } = useAuth();
  const role = user?.user?.is_user;
  const isSuperAdmin = role === "isAdmin" || role === "super_admin" || role === "pro_super_admin";
  const tokenRef = useRef(user?.authorization);
  useEffect(() => { tokenRef.current = user?.authorization; }, [user]);

  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [printingId, setPrintingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  // Draft edits per invoice: { document_type?, payment_status? }
  const [pending, setPending] = useState({});

  const fetchList = async (pageNum = page) => {
    setLoading(true);
    setError("");
    const res = await fetchInvoiceListAPI({
      page: pageNum,
      per_page: PER_PAGE,
      q: q.trim() || undefined,
      payment_status: statusFilter || undefined,
      document_type: typeFilter || undefined,
    }, tokenRef.current);
    setLoading(false);
    if (res.success) {
      setInvoices(res.invoices || []);
      setSummary(res.summary || null);
      setTotalPages(Math.max(1, Math.ceil((res.total || 1) / PER_PAGE)));
      setPending({});
    } else {
      setError(res.message || "Failed to load invoices.");
      setInvoices([]);
    }
  };

  useEffect(() => { fetchList(page); }, [page]); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchList(1);
  };

  const handlePrint = async (id) => {
    setPrintingId(id);
    setError("");
    const printWindow = window.open("about:blank", "_blank");
    try {
      const res = await printInvoiceAPI({ id }, tokenRef.current);
      if (res.success && res.invoice?.invoice_url) {
        setMessage(`PDF ready for ${res.invoice.invoice_no}`);
        if (printWindow) {
          printWindow.location.href = res.invoice.invoice_url;
        } else {
          window.location.assign(res.invoice.invoice_url);
        }
        fetchList(page);
      } else {
        printWindow?.close();
        setError(res.message || "Print failed.");
      }
    } catch (err) {
      printWindow?.close();
      setError(err.message || "Print failed.");
    } finally {
      setPrintingId(null);
    }
  };

  const draft = (invoice) => ({
    document_type: pending[invoice.id]?.document_type ?? invoice.document_type,
    payment_status: pending[invoice.id]?.payment_status ?? invoice.payment_status,
  });

  const isDirty = (invoice) => {
    const d = draft(invoice);
    return d.document_type !== invoice.document_type || d.payment_status !== invoice.payment_status;
  };

  const setDraftField = (invoice, field, value) => {
    if (!isSuperAdmin) return;
    setPending((prev) => {
      const current = {
        document_type: prev[invoice.id]?.document_type ?? invoice.document_type,
        payment_status: prev[invoice.id]?.payment_status ?? invoice.payment_status,
        [field]: value,
      };
      // Keep payment status in sync when switching document type
      if (field === "document_type") {
        if (value === "quotation" && current.payment_status === "unpaid") {
          current.payment_status = "quotation";
        }
        if (value === "invoice" && current.payment_status === "quotation") {
          current.payment_status = "unpaid";
        }
      }
      const unchanged =
        current.document_type === invoice.document_type
        && current.payment_status === invoice.payment_status;
      const next = { ...prev };
      if (unchanged) delete next[invoice.id];
      else next[invoice.id] = current;
      return next;
    });
  };

  const handleSaveRow = async (invoice) => {
    if (!isSuperAdmin || !isDirty(invoice)) return;
    const d = draft(invoice);
    setSavingId(invoice.id);
    setError("");
    setMessage("");
    const res = await updateInvoiceAPI({
      id: invoice.id,
      invoice_no: invoice.invoice_no,
      document_type: d.document_type,
      payment_status: d.payment_status,
      invoice_date: invoice.invoice_date,
      buyer_name: invoice.buyer_name,
      buyer_company: invoice.buyer_company,
      buyer_address: invoice.buyer_address,
      ntn: invoice.ntn,
      po_number: invoice.po_number,
      po_date: invoice.po_date,
      test_method: invoice.test_method,
      outstanding_note: invoice.outstanding_note,
      outstanding_pkr: invoice.outstanding_pkr,
      line_items: invoice.line_items,
    }, tokenRef.current);
    setSavingId(null);
    if (res.success) {
      setMessage(
        `Saved: ${TYPE_LABELS[d.document_type] || d.document_type} · ${STATUS_LABELS[d.payment_status] || d.payment_status}`
      );
      setPending((prev) => {
        const next = { ...prev };
        delete next[invoice.id];
        return next;
      });
      setInvoices((prev) =>
        prev.map((row) =>
          (row.id === invoice.id
            ? { ...row, document_type: d.document_type, payment_status: d.payment_status }
            : row)
        )
      );
      fetchList(page);
    } else {
      setError(res.message || "Failed to save changes.");
    }
  };

  const handleDelete = async (invoice) => {
    if (!isSuperAdmin) return;
    if (!window.confirm(`Delete ${invoice.invoice_no}?`)) return;
    const res = await deleteInvoiceAPI({ id: invoice.id }, tokenRef.current);
    if (res.success) {
      setMessage("Invoice deleted.");
      fetchList(page);
    } else {
      setError(res.message || "Delete failed.");
    }
  };

  return (
    <DashboardLayout pageTitle="All Invoices">
      <div className="cp-header">
        <div>
          <h2 className="cp-header__title">All Invoices</h2>
          <p className="cp-header__sub">
            {isSuperAdmin
              ? "Change Document Type or Status, then press Save. Super / Pro Super Admin can edit and delete."
              : "Managers can create, edit, and print. Document type, status, and delete require Super Admin."}
          </p>
        </div>
        <Link to="/create-invoice" className="cp-modal-submit"><BsPlus /> Create Invoice</Link>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success" onClose={() => setMessage("")} dismissible>{message}</Alert>}

      {summary && (
        <div className="inv-summary">
          <div className="inv-summary__card">
            <span>Invoice subtotals</span>
            <strong>PKR {money(summary.subtotal_pkr)}</strong>
          </div>
          <div className="inv-summary__card">
            <span>Outstanding</span>
            <strong>PKR {money(summary.outstanding_pkr)}</strong>
          </div>
          <div className="inv-summary__card">
            <span>Total payments</span>
            <strong>PKR {money(summary.total_payments_pkr)}</strong>
          </div>
          <div className="inv-summary__card">
            <span>Quotations (not billed)</span>
            <strong>PKR {money(summary.quotation_total)}</strong>
          </div>
        </div>
      )}

      <Form className="ac-form-card mb-3" onSubmit={handleSearch}>
        <Row className="align-items-end g-2">
          <Col md={4}>
            <Form.Label className="cp-form-label">Search</Form.Label>
            <Form.Control className="cp-form-control" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Invoice no. or buyer" />
          </Col>
          <Col md={2}>
            <Form.Label className="cp-form-label">Status</Form.Label>
            <Form.Select className="cp-form-control" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="cp-form-label">Document type</Form.Label>
            <Form.Select className="cp-form-control" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All</option>
              <option value="invoice">Cash Invoice</option>
              <option value="quotation">Quotation</option>
            </Form.Select>
          </Col>
          <Col md={4} className="d-flex gap-2">
            <button type="submit" className="cp-edit-btn"><BsSearch /> Search</button>
            <button
              type="button"
              className="cp-modal-cancel"
              onClick={() => { setQ(""); setStatusFilter(""); setTypeFilter(""); setPage(1); setTimeout(() => fetchList(1), 0); }}
            >
              <BsX /> Clear
            </button>
          </Col>
        </Row>
      </Form>

      <div className="ac-form-card">
        {loading ? (
          <Spinner animation="border" />
        ) : (
          <div className="table-responsive">
            <table className="table cp-table">
              <thead>
                <tr>
                  <th>Invoice No.</th>
                  <th>Date</th>
                  <th>Document Type</th>
                  <th>Buyer</th>
                  <th>Total PKR</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const d = draft(inv);
                  const dirty = isSuperAdmin && isDirty(inv);
                  return (
                    <tr key={inv.id}>
                      <td><span className="cp-id-badge">{inv.invoice_no}</span></td>
                      <td>{inv.invoice_date}</td>
                      <td>
                        {isSuperAdmin ? (
                          <Form.Select
                            size="sm"
                            className={`inv-status inv-type inv-type--${d.document_type}`}
                            value={d.document_type}
                            disabled={savingId === inv.id}
                            onChange={(e) => setDraftField(inv, "document_type", e.target.value)}
                          >
                            {Object.entries(TYPE_LABELS).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </Form.Select>
                        ) : (
                          <span className={`inv-status-badge inv-type--${inv.document_type}`}>
                            {TYPE_LABELS[inv.document_type] || inv.document_type}
                          </span>
                        )}
                      </td>
                      <td>{inv.buyer_company || inv.buyer_name || "—"}</td>
                      <td>{money(inv.total_payments_pkr)}</td>
                      <td>
                        {isSuperAdmin ? (
                          <Form.Select
                            size="sm"
                            className={`inv-status inv-status--${d.payment_status}`}
                            value={d.payment_status}
                            disabled={savingId === inv.id}
                            onChange={(e) => setDraftField(inv, "payment_status", e.target.value)}
                          >
                            {Object.entries(STATUS_LABELS).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </Form.Select>
                        ) : (
                          <span className={`inv-status-badge inv-status--${inv.payment_status}`}>
                            {STATUS_LABELS[inv.payment_status] || inv.payment_status}
                          </span>
                        )}
                      </td>
                      <td className="d-flex gap-1 flex-wrap align-items-center">
                        {dirty && (
                          <button
                            type="button"
                            className="cp-edit-btn inv-status-save"
                            title="Save document type & status"
                            disabled={savingId === inv.id}
                            onClick={() => handleSaveRow(inv)}
                          >
                            {savingId === inv.id
                              ? <Spinner size="sm" animation="border" />
                              : <><BsSave /> Save</>}
                          </button>
                        )}
                        <Link to={`/create-invoice?id=${inv.id}`} className="cp-edit-btn" title="Edit"><BsPencil /></Link>
                        <button type="button" className="cp-edit-btn" title="Print" disabled={printingId === inv.id} onClick={() => handlePrint(inv.id)}>
                          {printingId === inv.id ? <Spinner size="sm" animation="border" /> : <BsPrinter />}
                        </button>
                        {isSuperAdmin && (
                          <button type="button" className="cp-delete-btn" title="Delete" onClick={() => handleDelete(inv)}><BsTrash /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!invoices.length && (
                  <tr><td colSpan={7} className="text-muted">No invoices yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="cp-pagination">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                className={`cp-page-btn${p === page ? " cp-page-btn--active" : ""}`}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default InvoicesPage;
